import { SWRConfiguration, SWRResponse } from "swr";

import { IQueryDataRequest, IReturn, ResponseStatus, UpdateCachedDataEvent, UpdateCachedDataEventType } from "@/api/dtos";
import useGet from "@/hooks/api/useGet";
import { HubConnection } from "@microsoft/signalr/dist/esm";
import { useCallback, useMemo, useState } from "react";
import useSignalROnMessage from "./useSignalROnMessage";

interface IQueryDataResponse<TData> {
    responseStatus?: ResponseStatus;
    data?: TData[];
}

interface ICacedDataQueryOptions<TData, TResponse extends IQueryDataResponse<TData>, TRequest extends TData & IReturn<TResponse> & IQueryDataRequest, TAdditionalState> {
    sortData?: (a: TData, b: TData) => number;
    getAdditionalState?: (response?: SWRResponse<TResponse, TResponse>) => TAdditionalState;
    channel?: string;
}



export const useCachedDataQuery = <TData, TResponse extends IQueryDataResponse<TData>, TRequest extends TData & IReturn<TResponse> & IQueryDataRequest, TAdditionalState = {}>(
    requestType: new(init?: Partial<TRequest>) => TRequest,
    options?: ICacedDataQueryOptions<TData, TResponse, TRequest, TAdditionalState>,
    query?: Partial<TData>, 
    configuration?: SWRConfiguration | undefined
) => {
    const getData = useCallback((response?: SWRResponse<TResponse, TResponse>) => {
        const data = response?.data?.data;

        if (options?.sortData && data && data.length !== 0) {
            return [...data].sort(options.sortData);
        }

        return data;
    }, [options?.sortData]);

    const getError = useCallback((response?: SWRResponse<TResponse, TResponse>) => {
        return (response?.error ?? response?.data)?.responseStatus;
    }, []);

    const getAdditionalState = useCallback((response?: SWRResponse<TResponse, TResponse>) => {
        if (options?.getAdditionalState) {
            return options.getAdditionalState(response);
        }

        return {} as TAdditionalState;
    }, [options?.getAdditionalState]);

    const request = useMemo(() => {
        if (!query) {
            return undefined;
        }

        return () => {
            return new requestType(query as any);
        }
    }, [requestType, query]);

    const output = useGet<TResponse, TData[], ResponseStatus, TAdditionalState>(request, { getData, getError, getAdditionalState }, configuration);

    const [updating, setUpdating] = useState(false);

    const onMessage = useCallback(async (connection: HubConnection, data: UpdateCachedDataEvent) => {
        if (data.type === UpdateCachedDataEventType.Started) {
            setUpdating(true);
        }
        else if (data.type === UpdateCachedDataEventType.Complete) {
            try {
                await output.refresh();
            }
            finally {
                setUpdating(false);
            }
        }

    }, [setUpdating, output.refresh]);

    useSignalROnMessage(options?.channel, onMessage)

    return {
        ...output,
        validating: output.validating || updating
    };
}

export default useCachedDataQuery;